/**
 * THE COOKIE NOTICE — shown once, and RE-OPENABLE FOREVER.
 *
 * ── THE TWO THINGS THIS FILE OWES ───────────────────────────────────────────
 *  1. A notice for a visitor who has not answered, on every page.
 *  2. A way back to it. A consent you cannot revisit is not a consent you can
 *     withdraw, and GDPR Art. 7(3) requires withdrawal to be as easy as giving
 *     it. The footer's "Cookie settings" link, any element carrying
 *     `data-cookie-settings`, any link to `#cookie-settings` (including one
 *     written into the Cookie Policy's own CMS body), and the URL hash on a
 *     cold load all reach {@link openCookieSettings}.
 *
 * ── WHERE THE ANSWER REALLY LIVES ───────────────────────────────────────────
 * TWO RECORDS, ONE OF THEM AUTHORITATIVE, AND THEY ARE NOT A CONTRADICTION.
 *
 * The backend already has consent machinery: an append-only log keyed by
 * `ConsentPurpose`, and `ANALYTICS` is the purpose bound to the `cookies`
 * document (`LEGAL_DOCUMENTS` in `pages/content/cms-api.ts`). It is what
 * actually gates collection — `AnalyticsService` asks
 * `hasAcceptedCurrent(userId, 'ANALYTICS')` per event and never memoises it.
 * So the SIGNED-IN answer is written THERE, through the existing endpoints
 * (`POST /consent`, `DELETE /consent/ANALYTICS`), and a second parallel
 * consent system is not built.
 *
 * It cannot be the only record, because a consent row needs a user id and this
 * banner's whole audience is a visitor who has no account yet — `POST /consent`
 * is behind `JwtGuard`. `localStorage` is therefore the DEVICE record: it is
 * what suppresses the banner on the next load and what a signed-out visitor's
 * answer can be kept in at all. When a user is signed in the two are written
 * together and the server's is the one that governs collection; the local copy
 * is then a cache of a decision, not an independent claim about it.
 *
 * ── WHAT THE CHOICE ACTUALLY GATES ──────────────────────────────────────────
 * Non-essential analytics (§9 funnel steps, FPS buckets, desync reports) and
 * nothing else. There are no third-party tags in this app. "Essential only"
 * writes a WITHDRAWN row for `ANALYTICS`, after which the server drops every
 * event it is sent. `analyticsConsent()` is the local read of the same answer.
 *
 * Essential storage — the auth cookies core sets, the locale, the theme, this
 * very record — is never gated and the copy never pretends it is.
 *
 * ── ACCESSIBILITY: TWO MODES, ON PURPOSE ────────────────────────────────────
 * FIRST RUN is a labelled REGION, not a modal. It does not trap focus and
 * Escape does not close it: a first-run banner that traps you has hijacked a
 * page you came to read, and dismissing-by-Escape is the consent-by-inaction
 * ePrivacy forbids. It is keyboard reachable and announced (`aria-live`).
 *
 * RE-OPENED is a real MODAL `dialog` — focus moved in, Tab cycled inside it,
 * Escape closes, focus restored to whatever opened it. Escape is safe here and
 * only here: an answer is already on record, so closing changes nothing.
 *
 * Both are bottom sheets capped at 80vh with their own scroll, so neither
 * covers a phone screen.
 *
 * ── STORAGE IS FALLIBLE ─────────────────────────────────────────────────────
 * Private windows throw on `localStorage`. Every read and write is wrapped: a
 * throw means "no record", so the notice shows — the safe failure, never a
 * crash.
 */
import { authStore } from '@/core/state/auth-store';

import { formatDate, t } from '../i18n';
import { TAP_TARGET, el } from './dom';
import { skinButton } from '../ui/button';
import { createSlotIcon } from '../ui/icon-registry';
import { attachPress } from '../ui/press';
import { legalDocumentPath } from './legal-links';

const STORAGE_KEY = 'rga:cookie-consent';
/** Bump if the cookie categories change materially — a new version re-asks. */
const CONSENT_VERSION = '2';

/** The hash any link may use to summon the notice. */
export const COOKIE_SETTINGS_HASH = '#cookie-settings';

export type CookieChoice = 'all' | 'essential';

interface StoredConsent {
  readonly choice: CookieChoice;
  readonly version: string;
  /** ISO-8601. WHEN the answer was given — half of what a consent record is. */
  readonly decidedAt: string;
}

/* ═══════════════════════════════════════════════════════════ the record ══ */

function read(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.choice !== 'all' && parsed.choice !== 'essential') return null;
    if (parsed.version !== CONSENT_VERSION) return null; // a new version re-asks
    return {
      choice: parsed.choice,
      version: CONSENT_VERSION,
      // A v1 record had no timestamp and a v2 one always does, but a
      // hand-edited value must not crash the read.
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : '',
    };
  } catch {
    return null;
  }
}

function write(choice: CookieChoice): StoredConsent {
  const record: StoredConsent = {
    choice,
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* no record this time — the notice simply shows again next load */
  }
  return record;
}

/**
 * Has the visitor accepted non-essential analytics? `false` until an explicit
 * "Accept all", so the default is no tracking — the only defensible default.
 *
 * This is the DEVICE answer. For a signed-in user the server's `ANALYTICS`
 * consent row is authoritative and is what actually stops collection; the two
 * are written together by {@link mountCookieConsent}.
 */
export function analyticsConsent(): boolean {
  return read()?.choice === 'all';
}

/** The stored choice, or `null` when the visitor has not answered. */
export function cookieChoice(): CookieChoice | null {
  return read()?.choice ?? null;
}

/** When the stored answer was given, or `null`. */
export function cookieConsentDecidedAt(): Date | null {
  const raw = read()?.decidedAt ?? '';
  if (raw === '') return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Test seam: forget the stored choice so the notice shows again. */
export function clearCookieConsentForTests(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/* ══════════════════════════════════════════════ mirroring to the server ══ */

/** The two calls the mirror makes. Narrowed from `AccountApi` so tests are cheap. */
export interface CookieConsentApi {
  grantConsent(purpose: 'ANALYTICS', against: null): Promise<unknown>;
  withdrawConsent(purpose: 'ANALYTICS'): Promise<unknown>;
}

/**
 * Write the answer into the REAL consent log, if there is an account to write
 * it against.
 *
 * Loaded through `import()` so the account API (and the axios client it drags)
 * stays out of the shell chunk for the overwhelming majority of loads, which
 * are a visitor who never touches the notice.
 *
 * A signed-out visitor is a no-op and that is correct, not a gap: there is no
 * subject to record a consent for. Their device answer still suppresses
 * collection, because `AnalyticsClient.start()` fails closed on the 401 it
 * gets from `GET /analytics/consent` anyway.
 *
 * A failure here is swallowed. The local record already stands, the server's
 * absence of a grant is itself "no analytics", and there is no user action
 * that could fix a network error on a banner.
 */
function mirrorToServer(choice: CookieChoice, injected?: CookieConsentApi): void {
  if (authStore.get().user === null) return;
  const run = (api: CookieConsentApi): Promise<unknown> =>
    choice === 'all' ? api.grantConsent('ANALYTICS', null) : api.withdrawConsent('ANALYTICS');
  if (injected !== undefined) {
    void Promise.resolve()
      .then(() => run(injected))
      .catch(() => undefined);
    return;
  }
  void import('@/engine/account/account-api')
    .then((m) => run(m.httpAccountApi as unknown as CookieConsentApi))
    .catch(() => undefined);
}

/* ═════════════════════════════════════════════════════════════ the view ══ */

const PRIMARY = `${TAP_TARGET} justify-center rounded px-5 font-bold bg-gold-bright text-ink hover:bg-sun`;
/**
 * The reject button is a REAL button of the same size and the same row, not a
 * text link and not a second screen. Equal prominence is the legal requirement
 * (EDPB 03/2022); the only difference is fill, and both carry `font-bold`.
 */
const SECONDARY = `${TAP_TARGET} justify-center rounded border border-border px-5 font-bold text-text hover:border-gold`;
const SHEET =
  'fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto border-t border-border ' +
  'bg-surface/95 backdrop-blur ' +
  'px-[max(1rem,env(safe-area-inset-left))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3';

export interface CookieConsentHandle {
  /** The notice element, or null when a choice already exists (nothing shown). */
  readonly element: HTMLElement | null;
  /** Re-open the notice so the visitor can change their answer. */
  open(): void;
  destroy(): void;
}

export interface CookieConsentOptions {
  /** Test seam for the server mirror. Defaults to the real account API. */
  readonly api?: CookieConsentApi;
  /**
   * Test seam for the collector stop. Defaults to `stopShellAnalytics`, loaded
   * with `import()` so the analytics shell stays out of this module's static
   * graph.
   */
  readonly stopCollecting?: () => void;
}

/** The live mount, so the delegated listener and `openCookieSettings` can reach it. */
let active: { open: () => void } | null = null;

/**
 * Open the notice in re-configure (modal) mode from anywhere.
 *
 * A no-op when no mount is live — a module-level function that appended to
 * `document.body` on its own would leak a second notice in any harness that
 * never calls `mountCookieConsent`.
 */
export function openCookieSettings(): void {
  active?.open();
}

/**
 * Mount the notice into `host` (the document body).
 *
 * Always installs the re-open plumbing, even when a choice is already stored —
 * that is the whole point of requirement (2). It renders nothing until either
 * there is no answer on record or something asks for it.
 */
export function mountCookieConsent(
  host: HTMLElement,
  options: CookieConsentOptions = {},
): CookieConsentHandle {
  let sheet: HTMLElement | null = null;
  /** 'notice' = first run, non-modal. 'settings' = re-opened, modal. */
  let mode: 'notice' | 'settings' = 'notice';
  let opener: HTMLElement | null = null;
  let detachKeys: (() => void) | null = null;
  let destroyed = false;

  const stopCollecting =
    options.stopCollecting ??
    ((): void => {
      void import('@/engine/analytics/shell')
        .then((m) => {
          m.stopShellAnalytics();
        })
        .catch(() => undefined);
    });

  const close = (): void => {
    detachKeys?.();
    detachKeys = null;
    sheet?.remove();
    sheet = null;
    if (mode === 'settings' && opener !== null && opener.isConnected) opener.focus();
    opener = null;
  };

  const choose = (choice: CookieChoice): void => {
    write(choice);
    mirrorToServer(choice, options.api);
    // A WITHDRAWAL TAKES EFFECT NOW, NOT ON THE NEXT LOAD. `AnalyticsClient`
    // asks `GET /analytics/consent` once, in `start()`, and never again: a
    // collector already running would keep POSTing for the rest of this page
    // session. The server drops those events (`AnalyticsService` re-asks
    // `hasAcceptedCurrent` per record), so nothing is stored either way — but
    // sending them at all after the visitor said no is not defensible.
    if (choice === 'essential') stopCollecting();
    close();
  };

  const open = (): void => {
    if (destroyed) return;
    if (sheet !== null) return; // already showing; do not stack two
    mode = 'settings';
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    build();
  };

  function build(): void {
    const modal = mode === 'settings';
    const stored = read();

    const acceptAll = el('button', {
      type: 'button',
      class: PRIMARY,
      'data-testid': 'cookie-accept-all',
      text: t('cookies.banner.accept_all'),
    });
    skinButton(acceptAll, { variant: 'primary' });
    acceptAll.prepend(createSlotIcon(document, 'action-confirm', { size: 20, class: 'rga-btn__icon mr-1.5' }));
    attachPress(acceptAll, { onPress: () => choose('all') });

    const essentialOnly = el('button', {
      type: 'button',
      class: SECONDARY,
      'data-testid': 'cookie-essential-only',
      text: t('cookies.banner.essential_only'),
    });
    skinButton(essentialOnly, { variant: 'secondary' });
    attachPress(essentialOnly, { onPress: () => choose('essential') });

    const policyLink = el('a', {
      class: `${TAP_TARGET} text-gold underline`,
      href: legalDocumentPath('cookies'),
      'data-testid': 'cookie-policy-link',
      text: t('cookies.banner.policy_link'),
    });

    const heading = el('h2', {
      id: 'cookie-consent-title',
      class: 'font-heading text-base text-gold',
      text: modal ? t('cookies.settings.title') : t('cookies.banner.title'),
    });

    const body: (HTMLElement | null)[] = [heading];

    if (modal) {
      // The re-configure view says WHAT is stored and WHY, per category. The
      // first-run notice keeps one sentence and the policy link; this one is
      // the detail a visitor came back for.
      body.push(
        el('p', { class: 'text-sm text-text-dim', text: t('cookies.settings.intro') }),
        el('dl', { class: 'flex flex-col gap-2 text-sm' }, [
          el('dt', { class: 'font-bold text-text', text: t('cookies.settings.essential_title') }),
          el('dd', { class: 'text-text-dim', text: t('cookies.settings.essential_body') }),
          el('dt', { class: 'font-bold text-text', text: t('cookies.settings.analytics_title') }),
          el('dd', { class: 'text-text-dim', text: t('cookies.settings.analytics_body') }),
        ]),
        stored === null
          ? null
          : el('p', {
              class: 'text-sm text-text-dim',
              'data-testid': 'cookie-current-choice',
              text:
                stored.choice === 'all'
                  ? t('cookies.settings.current_all')
                  : t('cookies.settings.current_essential'),
            }),
      );
      const decided = cookieConsentDecidedAt();
      if (decided !== null) {
        body.push(
          el('p', {
            class: 'text-xs text-text-dim',
            'data-testid': 'cookie-decided-at',
            text: t('cookies.settings.decided_on', { date: formatDate(decided) }),
          }),
        );
      }
      body.push(el('p', {}, [policyLink]));
    } else {
      body.push(
        el('p', { class: 'text-sm text-text-dim' }, [
          el('span', { text: `${t('cookies.banner.body')} ` }),
          policyLink,
        ]),
      );
    }

    const actions: HTMLElement[] = [essentialOnly, acceptAll];
    if (modal) {
      const closeButton = el('button', {
        type: 'button',
        class: SECONDARY,
        'data-testid': 'cookie-settings-close',
        text: t('cookies.settings.close'),
      });
      skinButton(closeButton, { variant: 'secondary', sound: 'modal_close' });
      closeButton.prepend(createSlotIcon(document, 'header-close', { size: 18, class: 'rga-btn__icon mr-1.5' }));
      attachPress(closeButton, { onPress: () => close() });
      actions.push(closeButton);
    }

    sheet = el(
      'div',
      {
        class: SHEET,
        // A first-run notice is a region; a re-opened one is a dialog. See the
        // header — the difference is deliberate and is not cosmetic.
        role: modal ? 'dialog' : 'region',
        'aria-modal': modal ? 'true' : false,
        'aria-labelledby': 'cookie-consent-title',
        'aria-label': modal ? false : t('cookies.banner.aria_label'),
        // Announced without stealing focus on first run; the modal takes focus
        // instead and needs no live region.
        'aria-live': modal ? false : 'polite',
        'data-testid': 'cookie-consent',
        'data-mode': mode,
        tabindex: modal ? '-1' : false,
      },
      [
        el(
          'div',
          {
            class:
              'mx-auto flex w-full max-w-6xl flex-col gap-3 ' +
              (modal ? '' : 'sm:flex-row sm:items-center sm:justify-between'),
          },
          [
            el('div', { class: 'flex flex-col gap-2' }, body),
            el('div', { class: 'flex flex-wrap items-center gap-2 sm:flex-nowrap' }, actions),
          ],
        ),
      ],
    );

    host.appendChild(sheet);

    if (modal) {
      sheet.focus();
      detachKeys = trapFocus(sheet, close);
    }
  }

  // First run: no answer on record, so ask. With an answer on record this
  // renders nothing and only the re-open plumbing below is installed.
  if (read() === null) {
    mode = 'notice';
    build();
  }

  /*
   * THE WAY BACK IN.
   *
   * Delegated at the document, so it works for the footer link, for anything an
   * operator writes into the Cookie Policy's CMS body, and for controls mounted
   * long after this call. A plain anchor to `#cookie-settings` degrades to a
   * harmless no-op hash if this listener is ever gone.
   *
   * It deliberately does NOT hijack links to the cookie POLICY itself: a
   * visitor clicking "Cookie Policy" wants to read the policy. The policy is
   * where the settings link lives, not a replacement for it.
   */
  const onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-cookie-settings], a[href$="#cookie-settings"]');
    if (trigger === null) return;
    event.preventDefault();
    open();
  };
  document.addEventListener('click', onClick);
  const offClick = (): void => document.removeEventListener('click', onClick);

  // A cold load on `…#cookie-settings` — e.g. the link shared in an email.
  try {
    if (window.location.hash === COOKIE_SETTINGS_HASH) open();
  } catch {
    /* no location in this environment */
  }

  active = { open };

  return {
    element: sheet,
    open,
    destroy(): void {
      destroyed = true;
      offClick();
      close();
      if (active !== null && active.open === open) active = null;
    },
  };
}

/**
 * Keep Tab inside `container` while it is open, and let Escape close it.
 *
 * The focusable set is recomputed per keystroke rather than cached: the sheet
 * has a link whose presence depends on the stored record, and a stale list
 * would let Tab escape to the page behind a modal.
 */
function trapFocus(container: HTMLElement, close: () => void): () => void {
  const handler = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [
      ...container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const activeEl = document.activeElement;
    if (event.shiftKey && (activeEl === first || activeEl === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}
